class DateUtils {
	private static _toDate(value: string | Date): Date {
		let date: Date;

		// Check if value is a string or a Date object
		if (typeof value === 'string') {
			// Append default time if only a date portion is provided
			if (!value.includes('T')) {
				value += 'T00:00:00';
			}
			date = new Date(value);
		} else {
			date = value;
		}

		return date;
	}

	static getMaxValidDate(...dates: (string | Date)[]): Date | null {
		const validTimestamps = dates
			.map((date) => {
				const timestamp = new Date(date).getTime();
				return isNaN(timestamp) ? null : timestamp; // Convert to timestamp, filter out NaN
			})
			.filter((timestamp): timestamp is number => timestamp !== null); // Keep only valid timestamps

		return validTimestamps.length > 0 ? new Date(Math.max(...validTimestamps)) : null; // Return max or null if no valid dates
	}

	static formatDate(dateString: string | Date, locale: string): string {
		const date = this._toDate(dateString);

		const currentYear = new Date().getFullYear();
		const dateYear = date.getFullYear();

		const options: Intl.DateTimeFormatOptions = {
			month: 'long', // Use short format for the month
			day: 'numeric',
		};

		// Include the year only if it's different from the current year
		if (currentYear !== dateYear) {
			options.year = 'numeric';
		}

		return new Intl.DateTimeFormat(locale, options).format(date);
	}


	static calculateDuration(start: string, end: string): { h: number; m: number } {
	  const [startHours, startMinutes] = start.split(':').map(Number);
	  const [endHours, endMinutes] = end.split(':').map(Number);

	  const startTotalMinutes = startHours * 60 + startMinutes;
	  const endTotalMinutes = endHours * 60 + endMinutes;
	  const differenceMinutes = endTotalMinutes - startTotalMinutes;

	  const hours = Math.floor(differenceMinutes / 60);
	  const minutes = differenceMinutes % 60;

	  return { h: hours, m: minutes };
	}





	static formatMonthAndYear (date: Date, locale: string): string {
	    const month = date.toLocaleString(locale, { month: 'long' });
	    const year = date.getFullYear();
	    return `${month} ${year}`;
	}

	static formatRangeDate(dateFrom: string | Date, dateTo: string | Date, locale: string): string {
		const from = this._toDate(dateFrom);
		const to = this._toDate(dateTo);

		// Define formatters for different date parts

		// numeric.
		const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });
		// "2-digit"
		const dayFormatter = new Intl.DateTimeFormat(locale, { day: 'numeric' });

		// 2-digit
		const yearFormatter = new Intl.DateTimeFormat(locale, { year: 'numeric' });

		// full" | "long" | "medium" | "short"

		const fromDay = dayFormatter.format(from);
		const fromMonth = monthFormatter.format(from);
		const fromYear = yearFormatter.format(from);

		const toDay = dayFormatter.format(to);
		const toMonth = monthFormatter.format(to);
		const toYear = yearFormatter.format(to);

		// If the dates are in the same year
		if (from.getFullYear() === to.getFullYear()) {
			// If the dates are in the same month
			if (from.getMonth() === to.getMonth()) {
				if (from.getDate() === to.getDate()) {
					return `${toDay} ${fromMonth} ${fromYear}`; // Example: "1 - 5 Nov 2024"
				}

				return `${fromDay} - ${toDay} ${fromMonth} ${fromYear}`; // Example: "1 - 5 Nov 2024"
			} else {
				return `${fromDay} ${fromMonth} - ${toDay} ${toMonth} ${fromYear}`; // Example: "1 Nov - 5 Dec 2024"
			}
		} else {
			// Dates span different years
			return `${fromDay} ${fromMonth} ${fromYear} - ${toDay} ${toMonth} ${toYear}`; // Example: "31 Dec 2024 - 1 Jan 2025"
		}
	}
}

export default DateUtils;
